import { useConvex, useQuery } from "convex/react";
import { Clock, History, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  useVersionsByEntity,
  revertToVersion as revertLocalVersion,
} from "@/data/adapters/local/assetVersions";
import type { LocalAssetVersion } from "@/data/adapters/local/assetVersions";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { useEditorStore } from "@/store/editorStore";
import { api } from "../../../convex/_generated/api";

function formatFileSize(bytes: number | undefined): string {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function timeAgo(dateStr: string | undefined): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

interface VersionHistoryProps {
  panoramaId: string;
}

export const VersionHistory = ({ panoramaId }: VersionHistoryProps) => {
  const convex = useConvex();
  const { projectBackend } = useEditorStore();
  const { toast } = useToast();
  const useLocalBackend = isElectron() && projectBackend === "local";

  const localVersions = useVersionsByEntity("panorama", panoramaId);
  const cloudVersions = useQuery(
    api.assetVersions.listByEntity,
    !useLocalBackend ? { entity_type: "panorama", entity_id: panoramaId } : "skip",
  );

  const versions = (useLocalBackend ? localVersions : cloudVersions) as
    | LocalAssetVersion[]
    | undefined;

  const handleRevert = async (versionId: string) => {
    try {
      if (useLocalBackend) {
        await revertLocalVersion(versionId);
      } else {
        await convex.mutation(api.assetVersions.revert, { version_id: versionId as any });
      }
      toast({ title: "Reverted", description: "Panorama reverted to selected version." });
    } catch (error) {
      console.error("Revert error:", error);
      toast({ variant: "destructive", title: "Error", description: "Failed to revert." });
    }
  };

  if (!versions || versions.length === 0) {
    return (
      <Card className="bg-gray-700 border-gray-600">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-xs flex items-center gap-1.5">
            <History size={12} />
            Version History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-400 text-xs py-3">
            <Clock size={14} className="mx-auto mb-1 opacity-50" />
            No version history yet.
            <br />
            Replace the panorama to create a new version.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gray-700 border-gray-600">
      <CardHeader className="pb-3">
        <CardTitle className="text-white text-xs flex items-center gap-1.5">
          <History size={12} />
          Version History ({versions.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {versions.map((v) => {
          const isCurrent = Boolean(v.is_current);
          return (
          <div
            key={v._id}
            className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${
              isCurrent
                ? "bg-blue-600/20 border border-blue-500/30"
                : "bg-gray-600 hover:bg-gray-500"
            }`}
          >
            <span className="text-gray-400 font-mono w-5 text-center flex-shrink-0">
              v{v.version_number}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                {v.label && <span className="text-blue-300 truncate">{v.label}</span>}
                {isCurrent && (
                  <span className="px-1 py-0.5 rounded bg-blue-600/30 text-blue-300 text-[10px]">
                    current
                  </span>
                )}
              </div>
              <div className="text-gray-400">
                {formatFileSize(v.file_size)} · {timeAgo(v.created_at)}
              </div>
            </div>
            {!isCurrent && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-gray-500"
                onClick={() => handleRevert(v._id)}
                title="Revert to this version"
              >
                <RotateCcw size={11} />
              </Button>
            )}
          </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
